import React, { useContext, useState, useRef } from 'react'
/** @jsx jsx */
import { jsx } from '@emotion/core'
import { connect } from 'react-redux'
import moment from 'moment'
import Grid, { virtualizedGridRenderer } from '@integec/grid-tools'
import { strCol } from '@integec/grid-tools/lib/cols'
import { Button, DatePicker, Upload, Input, Icon, message } from 'antd'
import {
  getNonHydroEvents,
  saveNonHydroEvents,
  getAuthorization,
  getCredentials,
  nonHydroUnitLockStatus,
} from 'api/pgads'
import { DatePickerContext, ErrorContext, SpinContext } from 'context'
import {
  getEnvConfig,
  getPermitActions,
  ExportComponent,
  getApiTargetInfo,
} from 'core'
import UtilityUnitSelector from 'core/component/UtilityUnitSelection'
import {
  modifiedDateCol,
  modifierCol,
  utilityHeader,
  unitIdHeader,
} from 'core/HeaderColTypeProps'
import {
  Content,
  CriteriaRow,
  Filler,
  FullHeightSizer,
  Title,
} from 'core/screen-util'
import { getUnitNameMap, getUnits, getUtilityNameMap } from 'core/selectors'
import {
  rowHeaderRender,
  hasAccesPermits,
  exportDataComponent,
} from 'core/utils'
import * as ScreenPermissions from 'core/EventActions'
import NonHydroInsertModal from './NonHydroInsertEventModal'
import JcTmpErrorDialog from 'core/component/JcTmpErrorDialog'
import { BROOKFIELD_CLIENT } from '../client'

const NonHydroEvents = ({
  apiTargetInfo,
  utilityNameMap,
  unitNameMap,
  units,
  envConfig,
  actionPermits,
}) => {
  const CLIENT = envConfig.clientId || 'INTEG'
  const title =
    CLIENT === BROOKFIELD_CLIENT ? 'Hydro Breaker Events' : 'Non-Hydro Events'

  const { selectedDate, setSelectedDate } = useContext(DatePickerContext)
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const [selUnit, setSelUnit] = useState([])
  const [data, setData] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState('')
  const [showInsertModal, setShowInsertModal] = useState(false)
  const [showMessages, setShowMessages] = useState(false)
  const [messages, setMessages] = useState([])
  const [unitLockMap, setUnitLockMap] = useState({})

  const retrieve = ({ date = selectedDate, selUnits = selUnit[1] }) => {
    if (selUnits === undefined || selUnits.length < 1) {
      message.error('Please, Select Unit')
      return
    }
    setSpinning(true)

    getNonHydroEvents(apiTargetInfo)({
      transDate: date.format('YYYY-MM-DD'),
      unitIds: [selUnits],
    }).fork(setError, data => {
      setSpinning(false)

      nonHydroUnitLockStatus(apiTargetInfo)({
        date: date.format('YYYY-MM-DD'),
        units: [selUnits],
      }).fork(setError, setUnitLockMap)
      setData(
        data.map(d => ({
          ...d,
          utility: `${d.utility}-${utilityNameMap[d.utility]}`,
          unitId: `${d.unit}-${unitNameMap[d.unitId]}`,
          zoneDateStringZone: d.zoneDateString + d.zone,
        }))
      )
    })
  }

  const headerWithFormatter = headers.map(header => {
    if (header.ident === 'utility') {
      return {
        ...header,
        rowHeaderBackgroundColor: '#eee',
        sortIndexGetter: ({ rowData }) =>
          `${rowData.utility}-${utilityNameMap[rowData.utility]}` ||
          rowData.utility,
      }
    }
    if (header.ident === 'unitId') {
      return {
        ...header,
        rowHeaderBackgroundColor: '#eee',
        sortIndexGetter: ({ rowData }) =>
          `${rowData.utility}-${unitNameMap[rowData.unit]}` || rowData.unit,
      }
    }
    return header
  })
  const unitInfo = units.find(u => u.unitId === selUnit[1])

  const { authGodMode } = envConfig

  const fileProps = {
    name: 'file',
    action: `${apiTargetInfo.baseUrl}/nonhydro/import`,
    method: 'POST',
    headers: getAuthorization(apiTargetInfo),
    ...getCredentials(apiTargetInfo),
  }

  const GridRef = useRef()

  const openInsertModal = _ => {
    const lockStatus = unitLockMap[selUnit[1]] || 'U'
    if (lockStatus === 'U') setShowInsertModal(true)
    else message.error('You do not have access to edit this unit')
  }

  const generationOptionEndChange = hasAccesPermits({
    authGodMode,
    actionPermits,
    eventActions: [ScreenPermissions.HYDRO_NONHYDRO_OPTION_CHANGE],
    matchAny: true,
  })

  const nonHydroAllUnits = hasAccesPermits({
    authGodMode,
    actionPermits,
    eventActions: [ScreenPermissions.NON_HYDRO_ALL_UNITS],
    matchAny: true,
  })

  return (
    <div
      css={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      {showInsertModal && (
        <NonHydroInsertModal
          generationOptionEndChange={generationOptionEndChange}
          apiTargetInfo={apiTargetInfo}
          utility={unitInfo.utility}
          unit={unitInfo.unit}
          unitId={unitInfo.unitID}
          unitName={unitNameMap[selUnit[1]]}
          unitZone={unitInfo.timeZone}
          unitDlsEnabled={unitInfo.daylightSavings}
          visible={showInsertModal}
          date={selectedDate.format('YYYY-MM-DD')}
          onOk={data => {
            setSpinning(true)
            saveNonHydroEvents(apiTargetInfo)(data).fork(setError, _ => {
              setSpinning(false)
              setShowInsertModal(false)
              retrieve({})
            })
          }}
          onCancel={_ => setShowInsertModal(false)}
          spinning={spinning}
        />
      )}
      <CriteriaRow>
        <Title
          style={{
            marginRight: '15px',
            marginBottom: '5px',
            width: '270px',
          }}
        >
          {title}
        </Title>

        <UtilityUnitSelector
          utility={selUnit[0]}
          unit={selUnit[1]}
          onUnitChange={setSelUnit}
          style={{ width: '500px' }}
          unitfilter={un =>
            nonHydroAllUnits || (un.id1 !== 'HD' && un.id1 !== 'PS')
          }
        />

        <DatePicker
          style={{ marginLeft: '15px' }}
          allowClear={false}
          format="MM/DD/YYYY"
          value={selectedDate}
          onChange={d => {
            if (d && d.isValid) {
              setSelectedDate(d)
              retrieve({ date: d })
            }
          }}
        />

        <Button
          icon="database"
          loading={spinning}
          onClick={retrieve}
          css={{ marginLeft: '10px' }}
          disabled={
            !hasAccesPermits({
              authGodMode,
              actionPermits,
              eventActions: [ScreenPermissions.NONHYDRO_VIEW],
              matchAny: true,
            })
          }
          type="primary"
        >
          Retrieve
        </Button>
        <Button
          css={{ marginLeft: '10px' }}
          onClick={_ => {
            if (selUnit.length < 1) {
              message.error('Please,Select Unit')
              return
            }
            openInsertModal()
          }}
          icon="edit"
          disabled={
            !selUnit.length ||
            !hasAccesPermits({
              authGodMode,
              actionPermits,
              eventActions: [ScreenPermissions.NONHYDRO_ADD_DATA],
              matchAny: true,
            })
          }
        >
          Edit/Insert Events
        </Button>
        <Filler />
        <Upload
          {...fileProps}
          onChange={data => {
            if (data.file.response && data.file.response.length) {
              setShowMessages(true)
              setMessages(data.file.response)
            } else {
              if (data.file.response) {
                message.success('File imported successfully')
              }
            }
          }}
          multiple={false}
          showUploadList={false}
        >
          <Button
            disabled={
              !hasAccesPermits({
                authGodMode,
                actionPermits,
                eventActions: [ScreenPermissions.NONHYDRO_IMPORT],
                matchAny: true,
              })
            }
            style={{ marginLeft: '5px' }}
          >
            <Icon type="upload" /> Import
          </Button>
        </Upload>
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          style={{ marginLeft: '10px', marginRight: '10px', maxWidth: '200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <ExportComponent
          getData={_ => exportDataComponent(GridRef, data, fuzzyFilter)}
          data={data}
          headers={exportHeaders}
          fileName={`${title} ${moment().format('YYYY-MM-DD HH mm ss')}`}
          btnStyle={{ marginRight: '10px' }}
          disabled={
            !!(data && data.length <= 0) ||
            !hasAccesPermits({
              authGodMode,
              actionPermits,
              eventActions: [ScreenPermissions.NONHYDRO_VIEW],
              matchAny: true,
            })
          }
        />
      </CriteriaRow>
      <FullHeightSizer heightOffset={220}>
        {({ height, width }) => (
          <Content css={{ height: height - 20, paddingTop: '10px' }}>
            <Grid
              ref={GridRef}
              fuzzyFilter={fuzzyFilter}
              sortEnabled={false}
              data={data}
              headers={headerWithFormatter}
              altBy={u => u.unitId}
              altBgColor="#ebebeb"
              render={virtualizedGridRenderer({
                autoFixColByKey: false,
                rowHeaderRender,
                height: height - 50,
                width: width - 100,
                headerRowHeight: 30,
              })}
            />
          </Content>
        )}
      </FullHeightSizer>
      {showMessages && (
        <JcTmpErrorDialog
          errors={messages}
          visible={showMessages}
          closeErrorPrompt={_ => setShowMessages(false)}
          title="Error/Warning"
        />
      )}
    </div>
  )
}

const headers = [
  utilityHeader,
  unitIdHeader,
  strCol({
    ident: 'zoneDateString',
    display: 'Transdate',
    width: 200,
    isFiltered: true,
  }),
  strCol({ ident: 'zone', display: 'Zone', width: 70, isFiltered: true }),
  strCol({
    ident: 'status',
    display: 'Status',
    width: 150,
    isFiltered: true,
  }),

  modifierCol,
  modifiedDateCol,
]

const exportHeaders = [
  utilityHeader,
  unitIdHeader,
  strCol({
    ident: 'zoneDateStringZone',
    display: 'Transdate',
    width: 200,
    isFiltered: true,
  }),
  strCol({
    ident: 'status',
    display: 'Status',
    width: 150,
    isFiltered: true,
  }),

  modifierCol,
  modifiedDateCol,
]

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  units: getUnits(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
})
export default connect(mapStateToProps)(NonHydroEvents)
